import {Injectable} from '@angular/core';
import {ConflictType} from '@domain/conflicts/conflict-type';
import {
  AlertDialogResult,
  AssertionUtils,
  BackendError,
  BaseComponent,
  Conflict,
  ConflictsDialogComponent,
  ConflictsDialogData,
  DialogBuilderFactoryService,
  DialogType,
  NavigationHelperPrototypeService,
  ToastService,
  TranslateService
} from '@vdw/angular-component-library';
import {GridApi} from 'ag-grid-community';
import {Observable, switchMap, takeUntil} from 'rxjs';
import {BackendErrorCodeEnum} from '../backend-error-code.enum';

@Injectable({providedIn: 'root'})
export class RemoveConfirmationService extends BaseComponent {
  public constructor(
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly navigationHelper: NavigationHelperPrototypeService
  ) {
    super();
  }

  public open(selected: any, deleteCall: Observable<any>, translation: string, api?: GridApi<any>, conflictsCall?: Observable<any>, shouldNavigateBack: boolean = false): void {
    const object = this.getRemoveMessage(selected, translation);

    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (result === AlertDialogResult.CONFIRM) {
          const selectedRows = Array.isArray(selected) ? selected : [selected];

          deleteCall.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe({
            next: () => {
              api?.applyTransaction({remove: selectedRows});

              if (selectedRows === api?.getSelectedRows()) {
                api?.deselectAll();
              }

              this.openDeletedToast(selectedRows, translation);

              if (shouldNavigateBack) {
                this.navigationHelper.navigateBack(selected);
              }
            },
            error: (errorMessage: BackendError) => this.handleDeletionErrorResponse(errorMessage, translation, conflictsCall)
          });
        }
      });
  }

  private getRemoveMessage(selected: any, translation?: string): string {
    if (AssertionUtils.isNullOrUndefined(selected) && !AssertionUtils.isNullOrUndefined(translation)) {
      const singular = !Array.isArray(selected) || (Array.isArray(selected) && selected.length < 2);
      return singular ? this.translate.instant(translation, {count: 1}).toLowerCase() : `${selected.length} ${this.translate.instant(translation, {count: 2}).toLowerCase()}`;
    }

    return 'name' in selected ? selected.name : selected.technicalName ?? '';
  }

  private handleDeletionErrorResponse(errorMessage: BackendError, translation: string, conflictsCall: Observable<any>): void {
    if (
      (BackendErrorCodeEnum[errorMessage?.errorContext?.error?.errorCode as string] === BackendErrorCodeEnum.LINKED_ENTITIES ||
        BackendErrorCodeEnum[errorMessage?.errorContext?.errorCode as string] === BackendErrorCodeEnum.LINKED_ENTITIES) &&
      conflictsCall
    ) {
      this.getConflicts(errorMessage.errorContext.error?.entityName ?? errorMessage.errorContext.entityName, translation, conflictsCall);
    } else {
      this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.translate.instant(translation, {count: 1})?.toLowerCase()}),
        messageText: errorMessage.errorContext?.messages?.length > 0 ? errorMessage.errorContext.messages[0] : errorMessage.message,
        type: DialogType.INFORMATION
      });
    }
  }

  private openDeletedToast(selectedRows: any[], translation: string): void {
    this.toastService.showInfo({
      tapToDismiss: false,
      timeOut: 2000,
      message:
        selectedRows.length > 1
          ? this.translate.instant('GENERAL.ACTIONS.DELETED_OBJECT_WITH_AMOUNT', {
              object: this.getRemoveMessage(selectedRows, translation),
              count: 1
            })
          : this.translate.instant('GENERAL.ACTIONS.DELETED_OBJECT', {
              object: `${this.translate.instant(translation, {count: 1})} ${this.getRemoveMessage(selectedRows[0])}`,
              count: 1
            })
    });
  }

  private getConflicts(entityName: string, translation: string, conflictsCall: Observable<any>): void {
    conflictsCall
      .pipe(
        switchMap((conflicts: Conflict[]) => {
          return this.dialogBuilderFactoryService
            .getBuilder()
            .withClass('alert-dialog')
            .openDialog(ConflictsDialogComponent, ConflictsDialogData.createCannotDeleteData(translation, entityName, conflicts, ConflictType));
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe();
  }
}
